import { useCallback, useEffect, useState } from 'react'
import { fetchUserRecentSearches } from 'actions/SearchActions'
import { getRecentSearches } from 'components/Search/utils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useTabVisibility from 'hooks/useTabVisibility'
import useTimeout from 'hooks/useTimeout'
import { daysInSeconds } from 'lib/datetime/durationInSeconds'
import {
  FAMILY_SEARCH_WINDOW_MS,
  getLatestFamilySearchTimestamp,
  getSavedFamilySearchTimestamp,
  hasRecentFamilySearch,
  rememberFamilySearchTimestamp,
} from 'lib/search/familySearchIntent'

// Recheck long-lived pages daily; schedule the final expiry at its exact boundary.
const MAX_REFRESH_DELAY_MS = daysInSeconds(1) * 1000

interface FamilySearchClock {
  history?: App.SearchState['userRecentSearches']
  now: number
  userKey?: string
  timestamp?: number
}

const EMPTY_CLOCK: FamilySearchClock = { now: 0 }

function useFamilySearchIntent(enabled: boolean) {
  const dispatch = useAppDispatch()
  const isTabActive = useTabVisibility()
  const { set: setExpiryTimeout, clear: clearExpiryTimeout } = useTimeout()
  const history = useAppSelector(state => state.search.userRecentSearches)

  // Logged-in and recognised logged-out users both qualify. Keep their evidence separate.
  const userKey = useAppSelector(state => state.auth.account.memberId ?? state.auth.domainUserId)
  const [clock, setClock] = useState<FamilySearchClock>(EMPTY_CLOCK)

  // Only participating traffic fetches history; Control and Variant share this check.
  useEffect(() => {
    if (!enabled || !isTabActive || !userKey) return

    dispatch(fetchUserRecentSearches())
  }, [dispatch, enabled, isTabActive, userKey])

  useEffect(() => {
    // Disabling the experiment also clears the snapshot, so re-enabling it cannot
    // briefly reuse eligibility evaluated before the experiment was switched off.
    if (!enabled) {
      setClock(EMPTY_CLOCK)
      return
    }
    if (!isTabActive) return

    const now = Date.now()
    let remoteTimestamp: number | undefined

    // Ignore cached remote history belonging to another user or an unfinished request.
    const isCurrentUserHistoryLoaded = history.cacheKey === userKey && history.state === 'done'

    if (isCurrentUserHistoryLoaded) {
      const remoteSearchEvidence = history.data.map(search => ({
        timestamp: search.timestamp,
        hasChildren: search.occupancy.some(room => room.agesOfChildren.length > 0),
      }))

      // The helper requires a child in any room and a valid submission time within
      // the last 30 days. Future timestamps are rejected; travel dates are ignored.
      remoteTimestamp = getLatestFamilySearchTimestamp(remoteSearchEvidence, now)
    }

    // Local history uses a child count or child ages as evidence of a child in any room.
    const localSearchEvidence = getRecentSearches(userKey).map(search => ({
      timestamp: search.dateAdded,
      hasChildren: search.rooms.some(room => (
        (room.children ?? 0) > 0 || (room.childrenAge?.length ?? 0) > 0
      )),
    }))
    const localTimestamp = getLatestFamilySearchTimestamp(localSearchEvidence, now)

    // The storage helper rejects saved timestamps outside the 30-day window.
    const savedTimestamp = getSavedFamilySearchTimestamp(userKey)

    // Any source is sufficient. Use the newest qualifying submission, so another
    // child search refreshes the window without requiring login or purchase history.
    const timestamp = Math.max(
      remoteTimestamp ?? 0,
      localTimestamp ?? 0,
      savedTimestamp ?? 0,
    ) || undefined

    // Retain the latest evidence independently of the destination-deduplicated
    // history, so later adults-only searches cannot erase it.
    rememberFamilySearchTimestamp(timestamp, userKey)

    setClock({ now, userKey, timestamp, history })
  }, [enabled, history, isTabActive, userKey])

  const refreshClock = useCallback(() => {
    setClock(previous => ({ ...previous, now: Date.now() }))
  }, [])

  // Expire eligibility even if the user leaves the homepage open. Returning to
  // a visible tab also recalculates the evidence in the effect above.
  useEffect(() => {
    if (!enabled || !isTabActive || clock.timestamp === undefined) return
    if (!hasRecentFamilySearch(clock.timestamp, clock.now)) return

    const expiresAt = clock.timestamp + FAMILY_SEARCH_WINDOW_MS
    const delay = Math.max(1, expiresAt - Date.now() + 1)

    setExpiryTimeout(refreshClock, Math.min(delay, MAX_REFRESH_DELAY_MS))
    return clearExpiryTimeout
  }, [clearExpiryTimeout, clock.now, clock.timestamp, enabled, isTabActive, refreshClock, setExpiryTimeout])

  const isRecognisedUser = !!userKey
  const isCurrentUserClock = clock.userKey === userKey
  const isCurrentHistorySnapshot = clock.history === history
  const isCurrentUserHistory = history.cacheKey === userKey
  const isHistoryLoading = history.state === 'initial' || history.state === 'loading'

  // A history success renders before this hook's effect evaluates it. Keep the
  // slot pending until that exact history has been consumed, so no other test
  // is exposed using the previous family timestamp.
  const pending = enabled && isRecognisedUser && (
    !isCurrentUserClock || !isCurrentHistorySnapshot || !isCurrentUserHistory || isHistoryLoading
  )

  // Missing or expired evidence does not qualify. Inventory, experiment variation
  // and competition with other carousels are checked by the shared slot separately.
  const eligible = enabled
    && isRecognisedUser
    && isCurrentUserClock
    && isCurrentHistorySnapshot
    && hasRecentFamilySearch(clock.timestamp, clock.now)

  return { eligible, pending }
}

export default useFamilySearchIntent
